import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import type { SalesForecastData } from '@/types'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts'
import { TrendingUp, Loader2, AlertTriangle } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { getChartColor, CHART_AXIS, ROTATED_LABEL_CHART_MARGIN } from '@/lib/theme'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'
import { periodLabel, type Grain } from '@/lib/fiscal'

interface RepOption {
  id: string
  name: string
}

interface SalesForecastCompareChartProps {
  months: number
  forecastPeriod: number
  category?: string
  /** Selected rep ids to overlay (expected length >= 2). */
  repIds: string[]
  /** Reps for id → name resolution. */
  reps: RepOption[]
  /** Display grain, owned by the page so both charts move together. */
  grain?: Grain
}

type MergedRow = { month: string; monthKey: string } & Record<string, string | number | undefined>

export function SalesForecastCompareChart({
  months,
  forecastPeriod,
  category,
  repIds,
  reps,
  grain = 'month',
}: SalesForecastCompareChartProps) {
  const repNameById = useMemo(() => new Map(reps.map((r) => [r.id, r.name])), [reps])

  // One forecast query per rep — same key shape as useSalesForecast so a rep
  // already loaded in single-rep mode reuses the cached entry.
  const results = useQueries({
    queries: repIds.map((repId) => ({
      // `grain` must appear here as well as in the request: this key deliberately mirrors
      // useSalesForecast's params object so a rep already loaded in single-rep mode reuses
      // the cached entry, and omitting it would make monthly and quarterly collide on one
      // key — serving whichever was fetched first.
      queryKey: [
        'reports',
        'salesForecast',
        { months, forecastPeriod, category, owner_id: repId, grain },
      ],
      queryFn: () =>
        apiClient.getSalesForecast({ months, forecastPeriod, category, owner_id: repId, grain }),
    })),
  })

  const isLoading = results.some((r) => r.isLoading)
  const errorCount = results.filter((r) => r.isError).length

  const { chartData, firstForecastLabel } = useMemo(() => {
    const rowsByKey = new Map<string, MergedRow>()
    // Canonical key straight from the API: 'YYYY-MM' or 'YYYY-QN'. Both sort lexically,
    // which is what the ordering below relies on.
    const keyOf = (row: { period_key?: string; month?: string }) =>
      row.period_key ?? String(row.month ?? '').slice(0, 7)
    const ensure = (monthKey: string): MergedRow => {
      let row = rowsByKey.get(monthKey)
      if (!row) {
        row = { monthKey, month: periodLabel(monthKey, grain, true) }
        rowsByKey.set(monthKey, row)
      }
      return row
    }

    let firstForecastKey: string | undefined

    repIds.forEach((repId, i) => {
      const data = results[i]?.data as SalesForecastData | undefined
      if (!data) return
      const actualKey = `rep_${repId}_actual`
      const forecastKey = `rep_${repId}_forecast`

      // Sorted on the period key rather than by parsing `month` into a Date — the keys
      // are already ordered strings, and parsing a month start is the timezone trap.
      const historical = (data.historical ?? [])
        .slice()
        .sort((a, b) => keyOf(a).localeCompare(keyOf(b)))
      historical.forEach((h) => {
        ensure(keyOf(h))[actualKey] = Number(h.revenue)
      })

      // Per-rep transition seed: connect this rep's actual line to its forecast. On the
      // quarterly grain the period in progress is in both arrays, so the forecast loop
      // below overwrites this seed with the blended value for that same row.
      const lastHistorical = historical[historical.length - 1]
      if (lastHistorical) {
        ensure(keyOf(lastHistorical))[forecastKey] = Number(lastHistorical.revenue)
      }

      const forecast = data.forecast ?? []
      forecast.forEach((f) => {
        const k = keyOf(f)
        ensure(k)[forecastKey] = Number(f.forecasted_revenue)
        if (!firstForecastKey || k < firstForecastKey) firstForecastKey = k
      })
    })

    const chartData = [...rowsByKey.values()].sort((a, b) => a.monthKey.localeCompare(b.monthKey))
    const firstForecastLabel = chartData.find((r) => r.monthKey === firstForecastKey)?.month
    return { chartData, firstForecastLabel }
  }, [results, repIds, grain])

  if (!isLoading && errorCount === repIds.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sales Forecast — Compare Reps</CardTitle>
          <CardDescription>Overlay of selected reps' revenue trajectories</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-destructive">Failed to load forecast data. Please try again.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" aria-hidden="true" />
          Sales Forecast — Compare Reps
        </CardTitle>
        <CardDescription>
          {repIds.length} reps · {months}-month history with {forecastPeriod}-month forecast
          {category
            ? ` — ${category}${category === 'Services' ? ' (includes uncategorized)' : ''}`
            : ''}
          . Solid = actuals, dashed = forecast.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {errorCount > 0 && errorCount < repIds.length && (
          <div
            className="mb-3 flex items-center gap-2 rounded-md bg-warning/10 px-3 py-2 text-sm text-warning-foreground"
            role="status"
          >
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            Couldn&apos;t load forecast for {errorCount} rep{errorCount > 1 ? 's' : ''}; showing the
            rest.
          </div>
        )}
        {isLoading ? (
          <div className="flex items-center justify-center h-[400px]">
            <Loader2
              className="h-8 w-8 animate-spin text-muted-foreground"
              aria-label="Loading sales forecast comparison"
            />
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground space-y-2">
            <p className="text-lg font-medium">No forecast data available</p>
            <p className="text-sm text-center max-w-md">
              The selected reps have no closed opportunities in the lookback window.
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={chartData} margin={ROTATED_LABEL_CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip
                content={<ChartTooltipContent />}
                formatter={(value: number | string, name: string) =>
                  typeof value === 'number' ? [formatCurrency(value, 'CAD'), name] : [value, name]
                }
              />
              <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="line" />

              {repIds.flatMap((repId, i) => {
                const color = getChartColor(i)
                const name = repNameById.get(repId) ?? 'Rep'
                return [
                  <Line
                    key={`${repId}-actual`}
                    type="monotone"
                    dataKey={`rep_${repId}_actual`}
                    stroke={color}
                    strokeWidth={2}
                    dot={false}
                    name={name}
                    connectNulls
                    isAnimationActive={false}
                  />,
                  <Line
                    key={`${repId}-forecast`}
                    type="monotone"
                    dataKey={`rep_${repId}_forecast`}
                    stroke={color}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    name={`${name} (forecast)`}
                    legendType="none"
                    connectNulls
                    isAnimationActive={false}
                  />,
                ]
              })}

              {firstForecastLabel && (
                <ReferenceLine
                  x={firstForecastLabel}
                  stroke={CHART_AXIS}
                  strokeDasharray="3 3"
                  // Kept in step with SalesForecastChart, where the reasoning is written
                  // out: 'top' put this in the 8px margin and clipped it, and the left
                  // anchor is the side with room at the actual/forecast boundary.
                  label={{
                    value: 'Forecast →',
                    position: 'insideTopLeft',
                    fontSize: 11,
                    fill: CHART_AXIS,
                  }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
